/**
 * AB#1797 (CR-2026-05-29) W3 — Mark-as-Paid mini-flow modal.
 *
 * States: default → submitting → success (close) | error-network | error-race (409).
 * Idempotency: caller mints the UUID on modal open and passes it; if the
 * submission is in-flight and the user closes/reopens, sessionStorage preserves
 * the same key for the lifetime of the tab.
 *
 * RTL contract: all layout via CSS logical properties. Modal toast positioned
 * with inset-inline-end.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation as useTranslationUntyped } from 'react-i18next';
import { useMarkAppointmentPaid } from '../../hooks/useMarkAppointmentPaid';
import { MarkPaidPaymentMethod } from '../../services/appointmentsList';
import { normalizeApiError } from '../../types/api-errors';
import s from './MarkAsPaidModal.module.css';

const useTranslation = useTranslationUntyped as any;

export interface MarkAsPaidModalProps {
    appointmentId: number;
    customer: { name: string; phone?: string };
    service: { name: string; amount: number; currency: string };
    staff: { name: string; branch: string };
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: (paymentMethod: MarkPaidPaymentMethod) => void;
    idempotencyKey: string;
    /** Analytics provenance. */
    source: 'list' | 'calendar';
    /**
     * AB#2034 — when the appointment is one leg of a customer package bundle, its
     * details show a "Bundle" badge (with the bundle name in the title). Omit for
     * standalone appointments.
     */
    bundle?: { name?: string | null } | null;
}

const METHODS: MarkPaidPaymentMethod[] = ['Cash', 'Card', 'BankTransfer', 'Other'];

const MarkAsPaidModal: React.FC<MarkAsPaidModalProps> = ({
    appointmentId,
    customer,
    service,
    staff,
    isOpen,
    onClose,
    onSuccess,
    idempotencyKey,
    source,
    bundle,
}) => {
    const { t } = useTranslation('appointments');
    const [selectedMethod, setSelectedMethod] = useState<MarkPaidPaymentMethod>('Cash');
    const [raceState, setRaceState] = useState(false);
    const [autoCloseCountdown, setAutoCloseCountdown] = useState<number | null>(null);
    const [networkError, setNetworkError] = useState<string | null>(null);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const mutation = useMarkAppointmentPaid();

    // Reset state on open
    useEffect(() => {
        if (isOpen) {
            setSelectedMethod('Cash');
            setRaceState(false);
            setAutoCloseCountdown(null);
            setNetworkError(null);
        }
    }, [isOpen]);

    // Escape key closes (unless race state)
    useEffect(() => {
        if (!isOpen) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && !raceState) onClose();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [isOpen, onClose, raceState]);

    // Auto-close countdown for race state
    useEffect(() => {
        if (autoCloseCountdown === null) return;
        if (autoCloseCountdown <= 0) { onClose(); return; }
        timerRef.current = setInterval(() => {
            setAutoCloseCountdown(prev => (prev != null ? prev - 1 : null));
        }, 1000);
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, [autoCloseCountdown, onClose]);

    if (!isOpen) return null;

    const handleSubmit = async () => {
        setNetworkError(null);
        try {
            await mutation.mutateAsync({
                appointmentId,
                body: { paymentMethod: selectedMethod },
                idempotencyKey,
                source,
            });
            onSuccess?.(selectedMethod);
            onClose();
        } catch (err: unknown) {
            // UAT BUG #1 fix (2026-05-30): make the error-shape parse defensive.
            // TanStack mutateAsync rethrows the original axios error, which has
            // `response.status` and `response.data` on it. But the typed union
            // we accept widens to `unknown`, and some intermediate retry / proxy
            // layer can wrap. Try both shapes; never silently fall through.
            // AB#1919 (WS-5b) — status + code via the single normalizer
            // (status prefers 7807 `status`/response status; code = errorCode ?? code).
            const appError = normalizeApiError(err);
            const status = appError.status;
            const code = appError.code !== 'UNKNOWN' ? appError.code : undefined;
            // eslint-disable-next-line no-console
            console.error('[mark-paid] error', { status, code, raw: err });
            if (status === 409 && code === 'Payment.AlreadyPaidByStripe') {
                setRaceState(true);
                // UAT BUG #1 fix: extend from 3s to 6s so the founder can
                // actually see the red header + "Already paid" copy before it
                // auto-closes. Dina's §D.2 spec said 3s but UAT showed it was
                // too quick to register; revisit in a sibling polish PBI.
                setAutoCloseCountdown(6);
            } else {
                setNetworkError(t('markPaidModal.networkError'));
            }
        }
    };

    const isSubmitting = mutation.isPending;
    const methodLabel = (m: MarkPaidPaymentMethod) => t(`markPaidModal.method.${m.toLowerCase().replace('banktransfer', 'bankTransfer')}`);

    return (
        <div
            className={s.backdrop}
            role="presentation"
            onClick={() => { if (!raceState && !isSubmitting) onClose(); }}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-label={raceState ? t('markPaidModal.alreadyPaidTitle') : t('markPaidModal.title')}
                className={`${s.modal} ${raceState ? s.modalRace : ''}`}
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <h2 className={s.title}>
                    {raceState ? t('markPaidModal.alreadyPaidTitle') : t('markPaidModal.title')}
                </h2>

                {/* Summary card */}
                <div className={s.summary}>
                    <div className={s.summaryRow}>
                        <span className={s.summaryKey}>{t('markPaidModal.summary.customer')}</span>
                        <span className={s.summaryVal}>{customer.name}</span>
                    </div>
                    <div className={s.summaryRow}>
                        <span className={s.summaryKey}>{t('markPaidModal.summary.service')}</span>
                        <span className={s.summaryVal}>
                            {service.name}
                            {/* AB#2034 — bundle badge when this appointment is one leg
                                of a customer package bundle; bundle name in the title. */}
                            {bundle && (
                                <span
                                    className={s.bundleBadge}
                                    title={bundle.name ? `⁨${bundle.name}⁩` : undefined}
                                >
                                    {t('bundle.tag')}
                                </span>
                            )}
                            {' '}
                            <span dir="ltr" className={s.amount}>
                                {service.amount} {service.currency}
                            </span>
                        </span>
                    </div>
                    <div className={s.summaryRow}>
                        <span className={s.summaryKey}>{t('markPaidModal.summary.staff')}</span>
                        <span className={s.summaryVal}>{staff.name}</span>
                    </div>
                    <div className={s.summaryRow}>
                        <span className={s.summaryKey}>{t('markPaidModal.summary.branch')}</span>
                        <span className={s.summaryVal}>{staff.branch}</span>
                    </div>
                </div>

                {/* Race-condition state */}
                {raceState && (
                    <div className={s.raceNote}>
                        {t('realtimeToast.paidByActor', {
                            customer: customer.name,
                            amount: `${service.amount} ${service.currency}`,
                            method: 'Stripe',
                        })}
                        {autoCloseCountdown != null && (
                            <span className={s.autoClose}>
                                {' '}· {t('markPaidModal.autoClosing', { sec: autoCloseCountdown })}
                            </span>
                        )}
                    </div>
                )}

                {/* Payment method selector (hidden in race state) */}
                {!raceState && (
                    <>
                        {networkError && (
                            <p className={s.errorBanner} role="alert">{networkError}</p>
                        )}
                        <fieldset className={s.fieldset} disabled={isSubmitting}>
                            <legend className={s.legend}>{t('markPaidModal.method.label')}</legend>
                            <div className={s.radioGroup} role="radiogroup">
                                {METHODS.map(m => (
                                    <label key={m} className={s.radioLabel}>
                                        <input
                                            type="radio"
                                            name="paymentMethod"
                                            value={m}
                                            checked={selectedMethod === m}
                                            onChange={() => setSelectedMethod(m)}
                                            className={s.radio}
                                        />
                                        {methodLabel(m)}
                                    </label>
                                ))}
                            </div>
                        </fieldset>
                    </>
                )}

                {/* Actions */}
                <div className={s.actions}>
                    {raceState ? (
                        <button type="button" className={s.btnPrimary} onClick={onClose}>
                            {t('markPaidModal.close')}
                        </button>
                    ) : (
                        <>
                            <button
                                type="button"
                                className={s.btnPrimary}
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? '···' : t('markPaidModal.confirm')}
                            </button>
                            <button
                                type="button"
                                className={s.btnGhost}
                                onClick={onClose}
                                disabled={isSubmitting}
                            >
                                {t('markPaidModal.cancel')}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MarkAsPaidModal;
